/**
 * @file src/pages/invoice-creation-wizard/components/DeviceSelectionSection.jsx
 *
 * What changed:
 *  - device.certificates.production.expiresAt crash fixed —
 *    was guarded by device.csr?.certificateExpiryDate but accessed
 *    a completely different path. Now uses device.csr.certificateExpiryDate
 *    consistently for both the guard and the display value.
 *  - Commented-out getDeviceStatusBadge function removed (30 lines of dead code)
 *  - devices prop is now an array directly (useGetDevices select: data => data.data)
 */

import React, { useState } from "react";
import { motion } from "framer-motion";
import Button from "components/ui/Button";
import Icon from "components/AppIcon";
import { ROUTES } from "config/routes";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const getEnvironmentBadge = (environment) => {
  const isProduction = environment === "production";
  return (
    <span
      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
        isProduction
          ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
          : "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
      }`}
    >
      <Icon name="Shield" size={12} className="mr-1" />
      {isProduction ? "Production" : "Simulation/Sandbox"}
    </span>
  );
};

// ---------------------------------------------------------------------------
// DeviceSelectionSection
// ---------------------------------------------------------------------------

const DeviceSelectionSection = ({
  devices,
  selectedDevice,
  onDeviceSelect,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);

  // devices is already an array — useGetDevices select: (data) => data?.data ?? []
  const availableDevices =
    devices?.filter((device) => device.csr?.status === "approved") ?? [];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="bg-white dark:bg-gray-800 rounded-xl shadow-hero-cta border border-gray-100 dark:border-gray-700"
    >
      <div className="p-6">
        {/* Section Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
              <Icon name="Monitor" size={20} className="text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground dark:text-white">
                Device Selection
              </h3>
              <p className="text-sm text-muted-foreground dark:text-gray-300">
                Select a device with production certificate for invoice
                submission
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
            onClick={() => setIsExpanded(!isExpanded)}
          />
        </div>

        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="space-y-4"
          >
            {availableDevices.length === 0 ? (
              /* ── No devices empty state ──────────────────────────────────── */
              <div className="text-center py-8">
                <Icon
                  name="Monitor"
                  size={48}
                  className="mx-auto text-gray-400 mb-4"
                />
                <h4 className="text-lg font-medium text-foreground dark:text-white mb-2">
                  No Production Devices Available
                </h4>
                <p className="text-muted-foreground dark:text-gray-300 mb-4">
                  You need at least one device with an approved production
                  certificate to create invoices.
                </p>
                <Button
                  variant="primary"
                  size="md"
                  iconName="Monitor"
                  onClick={() => window.open(ROUTES.DEVICE_SETTINGS, "_blank")}
                >
                  Configure Device
                </Button>
              </div>
            ) : (
              /* ── Device list ─────────────────────────────────────────────── */
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-md font-medium text-foreground dark:text-white">
                    Available Devices ({availableDevices.length})
                  </h4>
                  {selectedDevice && (
                    <span className="text-sm text-green-600 dark:text-green-400">
                      Device selected ✓
                    </span>
                  )}
                </div>

                {availableDevices.map((device) => {
                  const isSelected =
                    selectedDevice?.deviceId === device.deviceId;

                  return (
                    <motion.div
                      key={device.deviceId}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3 }}
                      onClick={() => onDeviceSelect(device)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) =>
                        e.key === "Enter" && onDeviceSelect(device)
                      }
                      aria-pressed={isSelected}
                      className={`p-4 rounded-lg border cursor-pointer transition-all duration-200 ${
                        isSelected
                          ? "border-primary bg-primary-50 dark:bg-primary-900/20"
                          : "border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                              isSelected
                                ? "bg-primary-100 dark:bg-primary-900/40"
                                : "bg-gray-100 dark:bg-gray-700"
                            }`}
                          >
                            <Icon
                              name="Monitor"
                              size={20}
                              className={
                                isSelected
                                  ? "text-primary"
                                  : "text-gray-600 dark:text-gray-300"
                              }
                            />
                          </div>
                          <div>
                            <h5 className="font-medium text-foreground dark:text-white">
                              {device.organizationName ||
                                `Device ${device.deviceId}`}
                            </h5>
                            <p className="text-sm text-muted-foreground dark:text-gray-400">
                              Serial:{" "}
                              {device.deviceSerialNumber || device.deviceId}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          {getEnvironmentBadge(device.csr?.environment)}
                        </div>
                      </div>

                      {/* Certificate expiry — uses csr.certificateExpiryDate consistently */}
                      {device.csr?.certificateExpiryDate && (
                        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                          <p className="text-xs text-muted-foreground dark:text-gray-400">
                            Certificate expires:{" "}
                            {new Date(
                              device.csr.certificateExpiryDate,
                            ).toLocaleDateString()}
                          </p>
                        </div>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            )}
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};

export default DeviceSelectionSection;
